import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      toast.error('Valid email enter karein');
      return;
    }
    setLoading(true);
    try {
      await apiRequest('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setSent(true);
      toast.success('Reset link bhej diya hai (agar email registered hai)');
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-6">
          <img src={PHYSIGN_LOGO} alt="Physign" className="h-10 w-auto" />
        </Link>
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
              <Mail className="w-7 h-7 text-[#0F62FE]" />
            </div>
            <CardTitle className="text-xl">Forgot your password?</CardTitle>
            <CardDescription>
              {sent
                ? 'Check your email for the reset link'
                : 'Enter your email and we\'ll send you a reset link'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {sent ? (
              <div className="space-y-4" data-testid="forgot-password-sent">
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-emerald-800">
                    <p className="font-medium">Email sent successfully</p>
                    <p className="mt-1 text-emerald-700">
                      Agar ye email registered hai, aapko <strong>{email}</strong> par ek reset link milega.
                      Link <strong>1 hour</strong> ke liye valid hai.
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Didn't receive? Spam folder check karo ya 2 minute baad dubara try karo.
                </p>
                <Link to="/auth">
                  <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" />Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label>Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`pl-10 ${email && !validateEmail(email) ? 'border-red-400' : ''}`}
                      autoComplete="email"
                      autoFocus
                      data-testid="forgot-password-email-input"
                      required
                    />
                  </div>
                  {email && !validateEmail(email) && (
                    <p className="text-xs text-red-500 mt-1">Valid email enter karein</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0F62FE] hover:bg-[#0050E6]"
                  disabled={loading || !validateEmail(email)}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Reset Link
                </Button>
                <Link to="/auth" className="block">
                  <button
                    type="button"
                    className="w-full text-center text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />Back to Login
                  </button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
